import React, { useEffect, useRef, useState } from "react";
import { CoachTurn } from "../types";
import { getCoachHistory, postCoachMessage } from "../utils/apiUtil";
import MarkdownMessage from "./MarkdownMessage";

interface CoachPanelProps {
  sessionId: string;
  activePersonaId: string;
  scratchpad: string;
  getToken: () => string | undefined;
}

const CoachPanel: React.FC<CoachPanelProps> = ({ sessionId, activePersonaId, scratchpad, getToken }) => {
  const [turns, setTurns] = useState<CoachTurn[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    getCoachHistory(sessionId, getToken())
      .then((res) => {
        // Don't clobber an optimistic turn the user added before history loaded.
        if (!cancelled) setTurns((prev) => (prev.length ? prev : res.turns));
      })
      .catch(() => {
        /* start with an empty thread if history fails to load */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns, busy]);

  const send = async () => {
    const message = input.trim();
    if (!message || busy) return;
    setBusy(true);
    setError(null);
    setTurns((t) => [...t, { userPrompt: message, response: "", createdAt: new Date().toISOString() }]);
    setInput("");
    try {
      const { text } = await postCoachMessage(sessionId, message, activePersonaId, scratchpad, getToken());
      setTurns((t) => t.map((turn, i) => (i === t.length - 1 ? { ...turn, response: text } : turn)));
    } catch (e) {
      setTurns((t) => t.slice(0, -1));
      setInput(message);
      setError(e instanceof Error ? e.message : "Coach request failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col p-6 h-full min-h-0">
      <div className="flex items-center gap-2 mb-4">
        <span className="material-symbols-outlined text-tertiary text-sm">psychology</span>
        <h3 className="text-xs font-label font-bold text-tertiary tracking-[0.2em] uppercase">Coach</h3>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
        {turns.length === 0 && !busy && (
          <p className="text-sm text-slate-500">
            Stuck? Ask the coach for a nudge — e.g. "who haven't I talked to yet?"
          </p>
        )}
        {turns.map((turn, i) => (
          <div key={i} className="space-y-2">
            {turn.userPrompt && (
              <div className="ml-6 bg-surface-container-high rounded-lg px-3 py-2 text-sm text-on-surface">
                {turn.userPrompt}
              </div>
            )}
            {turn.response ? (
              <div className="mr-6 text-sm text-on-surface-variant">
                <MarkdownMessage text={turn.response} />
              </div>
            ) : busy && i === turns.length - 1 ? (
              <div className="mr-6 text-sm text-slate-500 animate-pulse">Thinking…</div>
            ) : null}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {error && <div className="text-error text-xs mt-2">{error}</div>}

      <div className="mt-3 flex gap-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask the coach…"
          rows={2}
          className="flex-1 bg-surface-container-low border border-outline-variant/20 rounded-lg p-2 text-sm text-on-surface placeholder-slate-500 resize-none focus:outline-none focus:border-tertiary/50 focus:ring-1 focus:ring-tertiary/30"
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy || !input.trim()}
          className="px-3 py-2 rounded-lg text-sm font-label font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/30 disabled:opacity-40"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default CoachPanel;
